import { useState } from 'react';
import { KeyRound, Plus, Users } from 'lucide-react';
import type { Lab } from '../lib/lab';
import { storeLab } from '../lib/lab';
import { joinLab } from '../services/projects';
import { useAuth } from '../hooks/useAuth';
import { Button } from './ui';
import LabGate from './LabGate';
import LabSetup from './LabSetup';
import SignOutDialog from './SignOutDialog';

/**
 * One step, once, straight after signing in: which lab is this?
 *
 * Deliberately after sign-in rather than before it. This used to be the first
 * screen in the app, and an App Store reviewer who had no code could get no
 * further — guideline 2.1, September 2026. The fix is not to remove the idea of
 * a lab, which is how colleagues find each other, but to stop it being a locked
 * door: by the time anyone sees this they already have an account, and both
 * answers here are things they can do themselves in one step.
 *
 * Creating has to come after sign-in for a second reason: a lab needs an owner.
 * That is what makes one-per-person enforceable, and what stops the endpoint
 * being something a script can point at all afternoon.
 */
export default function LabOnboarding({ onDone }: { onDone: (lab: Lab | null) => void }) {
  const { user } = useAuth();
  const [confirmingSignOut, setConfirmingSignOut] = useState(false);
  const [joining, setJoining] = useState(false);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);

  async function join(lab: Lab) {
    setBusy(true);
    // The code was already stored per-device by the gate; this is what makes it
    // follow the account onto a second phone.
    if (user) await joinLab(user.uid, lab).catch(() => {});
    setBusy(false);
    onDone(lab);
  }

  if (joining) {
    return (
      <LabGate
        onUnlocked={(lab) => {
          storeLab(lab);
          void join(lab);
        }}
        onCancel={() => setJoining(false)}
      />
    );
  }

  return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center px-6 py-6">
      <div className="w-full max-w-sm">
        <div className="mb-6 text-center">
          <Users size={28} className="mx-auto mb-3 text-accent-400" />
          <h1 className="text-xl font-semibold tracking-tight">Which lab are you in?</h1>
          <p className="mt-2 text-sm text-ink-400">
            A lab groups your team, so colleagues appear by name when you share a project instead of
            everyone having to remember addresses.
          </p>
        </div>

        <div className="space-y-2.5">
          <Button
            variant="primary"
            size="lg"
            className="w-full"
            icon={<Plus size={18} />}
            loading={busy}
            onClick={() => setCreating(true)}
          >
            Create a lab
          </Button>
          <Button
            variant="secondary"
            size="lg"
            className="w-full"
            icon={<KeyRound size={18} />}
            disabled={busy}
            onClick={() => setJoining(true)}
          >
            I have a lab code
          </Button>
        </div>

        <p className="mt-4 text-center text-xs text-ink-400">
          Either takes one step, and neither changes what anything costs. You can invite the rest of
          your team afterwards.
        </p>

        <p className="mt-6 text-center text-xs text-ink-400">
          Signed in as {user?.email ?? 'your account'} —{' '}
          <button
            onClick={() => setConfirmingSignOut(true)}
            className="underline-offset-2 hover:text-ink-100 hover:underline"
          >
            sign out
          </button>
        </p>
      </div>

      <SignOutDialog open={confirmingSignOut} onClose={() => setConfirmingSignOut(false)} />

      <LabSetup
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(made) => {
          const lab: Lab = {
            code: made.code,
            name: made.name,
            organisation: '',
            active: true,
          };
          storeLab(lab);
          // createOwnLab already put the code on the account server-side, so
          // this only has to carry the caller past the step.
          onDone(lab);
        }}
      />
    </div>
  );
}
